import dayjs from '@/components/NewUI/utils/dayjs';

import type { Dayjs } from 'dayjs';

import type { Answer, QuestionValidationResult } from './questionTypes';

interface ServerDateOptions {
  format: 'YYYY-MM-DD';
}
interface DisplayDateOptions {
  format: 'DD/MM/YYYY';
  strict?: boolean;
}
type QuestionDateOptions = ServerDateOptions | DisplayDateOptions;

export const parseQuestionDate = (value: string, options: QuestionDateOptions): Dayjs => {
  const isServerDate = options.format === 'YYYY-MM-DD';
  const strict = isServerDate || options.strict !== false;
  let parts: (string | undefined)[];
  if (strict) {
    const pattern = isServerDate ? /^(\d{4})-(\d{2})-(\d{2})$/ : /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = pattern.exec(value);
    if (!match) return dayjs(NaN);
    parts = match;
  } else {
    let remaining = value;
    parts = [value];
    // Walk moment's non-strict tokens DD, "/", MM, "/", YYYY: a literal "/" consumes through the next "/".
    [/\d{1,2}/, /\//, /\d{1,2}/, /\//, /\d{1,4}/].forEach((pattern, index) => {
      const match = pattern.exec(remaining);
      if (index % 2 === 0) parts.push(match?.[0]);
      if (match) remaining = remaining.slice(match.index + match[0].length);
    });
    if (parts[1] === undefined) return dayjs(NaN);
  }

  const now = dayjs();
  const day = Number(isServerDate ? parts[3] : parts[1]);
  const month = parts[2] === undefined ? now.month() + 1 : Number(parts[2]);
  const yearText = isServerDate ? parts[1] : parts[3];
  let year = yearText === undefined ? now.year() : Number(yearText);
  if (!strict && yearText?.length === 2) year += year > 68 ? 1900 : 2000;
  if (day < 1 || day > 31 || month < 1 || month > 12) return dayjs(NaN);

  // Set the full year explicitly: Dayjs string parsing maps years 0000-0099 to modern years.
  const parsed = dayjs('2000-01-01')
    .year(year)
    .month(month - 1)
    .date(day);
  return parsed.year() === year && parsed.month() === month - 1 && parsed.date() === day
    ? parsed
    : dayjs(NaN);
};

/**
 * Validates a single answer against its field requirements.
 * Returns an error message string, or null if valid.
 *
 * Pure function, no DOM access, no side effects.
 */
export const validateAnswerField = (answer: Answer): string | null => {
  const { field_type: fieldType } = answer.field;
  const isRequired = answer.field?.required;

  switch (fieldType) {
    case 'rank_field': {
      // Duplicate check applies regardless of required status
      const rankData = typeof answer.data === 'object' && answer.data !== null ? answer.data : {};
      const filledValues = Object.values(rankData)
        .map((v) => String(v).trim())
        .filter((v) => v !== '');
      const totalChoices = answer.field.field_choices?.length ?? 0;

      if (isRequired && filledValues.length !== totalChoices) return 'Please rank all options';
      if (filledValues.length !== new Set(filledValues).size) return 'Please assign unique ranks';
      break;
    }

    case 'select_field':
      if (isRequired && answer.field_choice_id == null) return 'Please select an option';
      break;

    case 'text_field':
    case 'text_area_field':
      if (isRequired && (!answer.data || String(answer.data).trim() === ''))
        return 'This field is required';
      break;

    case 'date_field':
      if (!answer.data || String(answer.data).trim() === '') {
        if (isRequired) return 'Please select a date';
      } else if (!parseQuestionDate(String(answer.data), { format: 'DD/MM/YYYY' }).isValid()) {
        return 'Please enter a valid date';
      }
      break;

    case 'radio_button_field':
      if (isRequired && answer.field_choice_id == null) return 'Please select an option';
      break;

    case 'check_box_field':
      if (isRequired && (!answer.field_choice_ids || answer.field_choice_ids.length === 0)) {
        return 'Please select at least one option';
      }
      break;

    default:
      if (isRequired && (!answer.data || String(answer.data).trim() === ''))
        return 'This field is required';
      break;
  }

  return null;
};

/**
 * Validates all question answers and returns a structured result.
 *
 * Pure function, no DOM access, no focus/scroll side effects.
 * Caller is responsible for surfacing errors in the UI.
 */
export const validateQuestions = (answers: Answer[]): QuestionValidationResult => {
  const errorsByFieldId: Record<number, string> = {};
  let firstInvalidFieldId: number | null = null;

  answers.forEach((answer) => {
    if (!answer.field) return;

    const error = validateAnswerField(answer);
    if (error) {
      errorsByFieldId[answer.field.id] = error;
      firstInvalidFieldId ??= answer.field.id;
    }
  });

  return {
    isValid: firstInvalidFieldId === null,
    firstInvalidFieldId,
    errorsByFieldId
  };
};
